// The Analytics tab (R8, screen 21): views vs. submissions, conversion, the
// views-per-day chart and the per-page funnel. Numbers come pre-aggregated
// from the server; this page only lays them out. Two submission figures exist
// on purpose — `submits` is the beacon count the funnel uses, `submissions`
// is the row count the rest of the console reports — and when they disagree
// (ad blockers eat beacons) the tiles show the row count, the honest one.
import { useEffect, useMemo, useState } from 'react';
import { FormAnalyticsData, getFormAnalytics } from '../../api/forms';
import { FigureTile } from '../../components/fs';
import { type CsvRow, Figure, Histogram } from '../../shared/charts';
import { pagesOf } from './builderModel';
import { useFormWorkspace } from './FormWorkspace';

const RANGES: [number, string][] = [[7, '7 days'], [30, '30 days'], [90, '90 days']];

/** "9/24", or "9/24/25" in another year — the chart's tick. */
const shortDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return Number.isFinite(m) && Number.isFinite(d) ? `${m}/${d}${y && y !== new Date().getFullYear() ? `/${String(y).slice(2)}` : ''}` : day;
};

/**
 * The funnel's rows: each page's unique visitors as a share of page 1, the
 * fall from the page before, and which step lost the most.
 */
export function funnelOf(pages: FormAnalyticsData['pages'], titles: string[]) {
  const first = pages[0]?.uniqueVisitors || 0;
  const rows = pages.map((p, i) => {
    const prev = i === 0 ? null : pages[i - 1];
    const drop = prev && prev.uniqueVisitors > 0 ? Math.round((1 - p.uniqueVisitors / prev.uniqueVisitors) * 100) : null;
    return {
      page: p.page + 1,
      title: titles[p.page] || '',
      visitors: p.uniqueVisitors,
      share: first > 0 ? (p.uniqueVisitors / first) * 100 : 0,
      drop,
    };
  });
  let biggest: { from: number; to: number; drop: number } | null = null;
  for (const r of rows) {
    if (r.drop !== null && r.drop > 0 && (!biggest || r.drop > biggest.drop)) biggest = { from: r.page - 1, to: r.page, drop: r.drop };
  }
  return { rows, biggest };
}

export default function FormAnalytics() {
  const { form } = useFormWorkspace();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<FormAnalyticsData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null);
    getFormAnalytics(form.id, days)
      .then((r) => setData(r.analytics))
      .catch((e) => setError(e.message || 'Could not load analytics.'));
  }, [form.id, days]);

  /** The pages' titles, from the live layout's page breaks — "Page 2 · Ticket & preferences". */
  const titles = useMemo(() => pagesOf(form.layout?.sections || []).map((p) => p.title), [form.layout]);
  const funnel = useMemo(() => (data ? funnelOf(data.pages, titles) : null), [data, titles]);

  const range = (
    <div className="fs-seg" role="group" aria-label="Range">
      {RANGES.map(([d, label]) => (
        <button
          key={d} type="button" className={days === d ? 'on' : ''}
          aria-pressed={days === d} onClick={() => setDays(d)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (error) return <div className="fan-page"><div className="ad-err">{error}</div></div>;

  const conversion = data && data.views > 0 ? (data.submissions / data.views) * 100 : null;
  const dayRows: CsvRow[] = (data?.days || []).map((d) => [d.day, d.views, d.uniqueVisitors, d.submits]);
  const anyViewsByDay = (data?.days || []).some((d) => d.views > 0);

  return (
    <div className="fan-page">
      <div className="fan-head">
        <h1>Analytics</h1>
        {range}
      </div>

      {!data && <div className="sl-quiet">Loading…</div>}

      {data && (
        <>
          <div className="fan-tiles fs-tiles-grid">
            <FigureTile label="Views" value={data.views.toLocaleString()} caption={`${data.uniqueVisitors.toLocaleString()} unique`} />
            <FigureTile
              label="Started" value={data.starts.toLocaleString()}
              caption={data.views > 0 ? `${Math.round((data.starts / data.views) * 100)}% of views` : 'of views'}
            />
            <FigureTile label="Submitted" value={data.submissions.toLocaleString()} caption="this period" />
            <FigureTile label="Conversion" value={conversion === null ? '—' : `${conversion.toFixed(1)}%`} caption="submitted ÷ views" />
          </div>

          <div className="fan-cards">
            <section className="fs-card fan-card" aria-label="Views per day">
              <div className="fan-card-head"><h2>Views per day</h2></div>
              {anyViewsByDay ? (
                <Figure title="Views per day" header={['Day', 'Views', 'Unique visitors', 'Submits']} rows={dayRows}>
                  <Histogram title="Views per day" data={data.days.map((d) => ({ label: shortDay(d.day), value: d.views }))} />
                </Figure>
              ) : (
                <p className="fan-empty">
                  Nothing yet in this window. Views count from the moment a visitor actually sees the
                  published form — share the public link and check back.
                </p>
              )}
            </section>

            <section className="fs-card fan-card" aria-label="Where people drop off">
              <div className="fan-card-head">
                <h2>Where people drop off</h2>
                {funnel?.biggest && (
                  <span className="fan-biggest" data-testid="biggest-drop">
                    Biggest drop: page {funnel.biggest.from} → {funnel.biggest.to}
                  </span>
                )}
              </div>
              {funnel && funnel.rows.length > 1 ? (
                <div className="fan-funnel">
                  {funnel.rows.map((r) => (
                    <div key={r.page} className="fan-funnel-row" data-testid="funnel-row">
                      <div className="fan-funnel-line">
                        <span className="fan-funnel-label">Page {r.page}{r.title ? ` · ${r.title}` : ''}</span>
                        <span className="fan-funnel-n">
                          {r.visitors.toLocaleString()}
                          {r.drop !== null && r.drop > 0 && <span className="fan-drop"> −{r.drop}%</span>}
                        </span>
                      </div>
                      <div className="fan-track" role="presentation">
                        <div className="fan-fill" style={{ width: `${Math.max(2, Math.min(100, r.share))}%` }} />
                      </div>
                    </div>
                  ))}
                  <p className="fan-note">
                    Unique visitors reaching each page, as a share of page 1. The step with the biggest fall is where
                    the form loses people.
                  </p>
                </div>
              ) : (
                <p className="fan-empty">
                  {data.pages.length === 1
                    ? 'This form is one page, so there is nowhere to drop off between pages.'
                    : 'No page views yet in this window.'}
                </p>
              )}
            </section>
          </div>
          <p className="fan-note fan-foot-note">
            Views and page views are beacons from the published form; Submitted is the number of responses stored.
          </p>
        </>
      )}
    </div>
  );
}
